import { type FormEvent, type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Berichtsanzeige } from '@crm/kontrakte';
import { anzeigeGleich, anzeigeVon, optionenGezeigt } from '@crm/kontrakte/src/anzeige';
import { OHNE_ZEITACHSE, QUARTILE_NUR_MIT_MEDIAN } from '@crm/kontrakte/src/kennzahloptionen';
import {
  DARSTELLUNGSNAMEN,
  DIAGRAMMART_BEDINGUNG,
  DIAGRAMMARTEN,
  darstellungstext,
  diagrammartUntauglich,
  istDiagrammart,
  liegendEmpfohlen,
  bildoptionen,
} from '@crm/kontrakte/src/diagrammarten';
import { ersteFreieStelle, vorgabegroesse } from '@crm/kontrakte/src/raster';

import {
  type Abfrageergebnis,
  type Berichtsbeschreibung,
  type Berichtsvergleich,
  type Berichtszeitraum,
  type Darstellung,
  type GespeicherterBericht,
  type Gruppierungskennung,
  type Katalogkennzahl,
  type Kennzahlenkatalog,
  type RelativerZeitraum,
  type Vergleichsart,
  VERGLEICHSNAMEN,
  ZEITRAUMNAMEN,
  type Belegart,
  type Berichtsfreigabe,
  type FehlendeKennzahl,
  FREIGABENAMEN,
  abfrage,
  aufzaehlung,
  bericht as holeBericht,
  berichtErgebnis,
  berichtswidget,
  berichtAendern,
  berichtAnlegen,
  berichtLoeschen,
  type Exportformat,
  exportDatei,
  katalog as holeKatalog,
  tagText,
  veraenderungText,
  vorschlagstitel,
  wertText,
  zeitpunktText,
} from '../api/berichte';
import { ApiFehler } from '../api/fehler';
import { type Berichtsentwurf, type KiVorschlag, berichtsvorschlagEntscheiden, kiStand } from '../api/ki';
import { anordnen, anordnung as holeAnordnung } from '../api/uebersicht';
import { Diagramm, Kreisdiagramm, Legende } from '../komponenten/Diagramm';
import { ladeUndReiche } from '../komponenten/datei';
import { Dialog } from '../komponenten/Dialog';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Filtermarken } from '../komponenten/Filtermarken';
import { Ladeanzeige, useLadestufe } from '../komponenten/Ladestufe';
import { Meldung } from '../komponenten/Meldung';
import type { Kopf } from '../komponenten/Rahmen';
import { Segmente } from '../komponenten/Segmente';
import { Sinnbild } from '../komponenten/Sinnbild';
import { Titelkarte } from '../komponenten/Titelkarte';
import { Skelett } from '../start/Widget';
import {
  ACHSENNAMEN,
  BEREICHSNAMEN,
  achsenDerAuswahl,
  aufteilungsgrund,
  bereiteKennzahlen,
  einheitText,
  gemeinsameFilter,
  gewaehlteKennzahlen,
  gruppierungTrifft,
  gruppierungsgrund,
  kennzahlTrifft,
  kennzahlgrund,
} from './auswahl';
import { Versanddialog } from './Berichtsversand';
import { Diagrammartbild } from './Diagrammarten';
import { grenzenAusText, grenzenGrund, mitOptionen } from './darstellungswahl';
import { Herkunft, type Zelle } from './Herkunft';
import { Erlaeuterung, InEigenenWorten } from './KiBericht';
import {
  Ergebnistabelle,
  achsenformat,
  achsentitel,
  einheitszeichen,
  GESTAPELT_BEDINGUNG,
  KREIS_BEDINGUNG,
  LINIE_BEDINGUNG,
  gestapeltUntauglich,
  kreisAus,
  kreisUntauglich,
  linieUntauglich,
  minMaxAn,
  optionAn,
  reihenAus,
  wirksameDarstellung,
} from './Ergebnis';

/**
 * DER BAUKASTEN (AUS-16, Auftrag #24).
 *
 * ============================================================================
 * DREI SPALTEN, EINE BESCHREIBUNG
 * ============================================================================
 *
 *   links    die Bausteine aus dem Katalog: die gewaehlten Kennzahlen als
 *            Liste, darunter die Suche nach Kennzahlen und die Suche nach
 *            der Gruppierung (Auftrag CRM-42), darunter „Aufteilen nach"
 *            als zweite Dimension (Auftrag CRM-46)
 *   Mitte    das Ergebnis als Balken, Linie oder Tabelle — die Tabelle steht
 *            immer darunter (NFA-14), der laufende Monat ist schraffiert
 *            (AUS-10)
 *   rechts   Zeitraum, Filter als Marken und die Definition der Kennzahl
 *            (AUS-01)
 *
 * Alles, was man hier einstellt, ist EINE Berichtsbeschreibung. Sie geht bei
 * jeder Aenderung an die API; gerechnet wird dort, mit dem Katalog, den
 * Rechten und dem Sichtbarkeitsbereich des Benutzers (AUS-06). Gespeichert
 * wird genau diese Beschreibung und nicht das Ergebnis.
 *
 * ============================================================================
 * NICHTS ANBIETEN, WAS DER SERVER ABWEIST
 * ============================================================================
 *
 * Die Kennzahlen kommen aus dem Katalog der API, also nur die, deren
 * Fachrecht der Benutzer hat. Was nicht zu den gewaehlten Kennzahlen, zur
 * Gruppierung oder zu den Filtern passt, steht ausgegraut mit Grund da
 * (auswahl.ts) — statt eine Anfrage zu schicken, die mit 400 zurueckkaeme.
 *
 * ============================================================================
 * KENNZAHLEN AUF ZWEI ACHSEN (Auftrag CRM-42) UND VERGLEICHE (#26, AUS-02)
 * ============================================================================
 *
 * Die Einheit der ersten Kennzahl bestimmt die linke Achse; bis zu fuenf
 * derselben Einheit stehen dort. Eine Kennzahl mit anderer Einheit legt von
 * selbst die rechte Achse an, auch dort bis zu fuenf; eine dritte Einheit
 * geht nicht (achsen.ts, dieselbe Regel wie im Server). Eine neue Kennzahl
 * kommt ans Ende der Liste — so behaelt jede ihre Farbe. Entfernen laesst
 * sich jede, solange eine bleibt; faellt die letzte der linken Achse weg,
 * rueckt die rechte nach links. Dazu bis zu zwei Vergleichszeitraeume,
 * aufgeloest vom Server; das Bild zeigt sie fuer die erste Kennzahl.
 *
 * ============================================================================
 * TEILEN, DASHBOARD, HERKUNFT (Auftrag #27)
 * ============================================================================
 *
 * Ein gespeicherter Bericht rechnet ueber `/berichte/:id/ergebnis` mit den
 * Rechten des Betrachters (AUS-06): Fehlt ein Fachrecht, steht es da, und die
 * uebrigen Kennzahlen rechnen weiter. Einen geteilten Bericht eines anderen
 * kann man ansehen und verstellen, aber nicht speichern — nur „Als eigenen
 * Bericht speichern" (eine Kopie). Teilen darf nur der Eigentuemer, mit allen
 * nur mit `auswertung:teilen` (AeA-42). Jede Zahl der Tabelle fuehrt zu ihren
 * Belegen (AUS-07).
 *
 * ============================================================================
 * IN EIGENEN WORTEN UND ERLAEUTERUNG (Auftrag #54, KI-15)
 * ============================================================================
 *
 * Ueber dem Ergebnis ein Feld fuer eine Frage. Der Vorschlag der KI ist eine
 * Beschreibung wie jede andere: Er oeffnet sich hier, gekennzeichnet (KI-01),
 * und rechnet ueber dieselbe Abfrage. Verwerfen stellt den Baukasten zurueck,
 * Speichern uebernimmt ihn so, wie er dann dasteht. Angeboten wird nur, was
 * dem Anwender laut `/ki/stand` zur Verfuegung steht (KI-07).
 *
 * ============================================================================
 * VERWERFEN UND DIE ANZEIGE (Auftrag CRM-47)
 * ============================================================================
 *
 * Ist ein Bericht angefangen — neu und nicht mehr leer, oder gegenueber dem
 * gespeicherten Stand verstellt —, steht oben „Verwerfen": Ein neuer kehrt
 * zur leeren Vorauswahl zurueck (CRM-46), ein gespeicherter zu seinem
 * gespeicherten Stand. Verloren ginge dabei immer etwas, deshalb fragt es
 * nach. „Neuer Bericht" daneben fragt nur, wenn Ungespeichertes verloren
 * ginge. Ein offener KI-Vorschlag hat sein eigenes Verwerfen (KI-04).
 *
 * Rechts waehlt man, was der Bericht zeigt (Legende, Werte, Gitter …). Die
 * Anzeige gehoert zur Beschreibung (Fassung 5) und gilt in Ansicht, PDF und
 * Widget; gerechnet wird mit ihr nichts — sie loest keine neue Abfrage aus.
 */

const RELATIV: readonly RelativerZeitraum[] = [
  'laufendes_jahr',
  'letzte_12_monate',
  'laufendes_quartal',
  'letztes_quartal',
  'laufender_monat',
  'vorjahr',
  'naechste_12_monate',
];

/**
 * Die Vorgabe eines neuen Berichts (Auftrag CRM-46, Wunsch des Nutzers vom
 * 04.10.2026): KEINE Kennzahl und keine Gruppierung — nur Zeitraum (laufendes
 * Jahr) und Darstellung stehen vor. Gerechnet wird erst, wenn eine Kennzahl
 * gewaehlt ist; bis dahin sagt die Mitte, was zu tun ist.
 */
function vorgabe(): Berichtsbeschreibung {
  return {
    kennzahlen: [],
    gruppierung: null,
    filter: [],
    zeitraum: { art: 'relativ', wert: 'laufendes_jahr' },
    vergleiche: [],
    darstellung: 'balken',
  };
}

/**
 * Was gerechnet wird: alles ausser der Anzeige (Auftrag CRM-47). Eine
 * Beschreibung der Fassung 3 hat keine Aufteilung — das ist dasselbe wie
 * keine (Auftrag CRM-46).
 */
function rechenkern(b: Berichtsbeschreibung): string {
  // Auftrag CRM-86: Min und Max rechnet der Server mit; vor Fassung 6 gibt es keine.
  // Auftrag CRM-88: ebenso die Kennzahloptionen; vor Fassung 7 gibt es keine.
  return JSON.stringify([
    b.kennzahlen, b.gruppierung, b.aufteilung ?? null, b.filter, b.zeitraum, b.vergleiche, b.darstellung, b.minMax ?? [],
    ...OPTIONSLISTEN.map((f) => b[f] ?? []), b.anteilAn ?? 'gesamt', b.kumuliertAls ?? 'zusaetzlich',
  ]);
}

/** Auftrag CRM-86, CRM-88: die Listen der Beschreibung, die je Kennzahl eine Option nennen. */
const OPTIONSLISTEN = ['minMax', 'anteil', 'median', 'quartile', 'n', 'kumuliert'] as const;
type Optionsliste = (typeof OPTIONSLISTEN)[number];

/** Die Beschreibung ohne die Kennzahl `kennung` in allen Optionslisten. */
function ohneOptionen(b: Berichtsbeschreibung, kennung: string, felder: readonly Optionsliste[]): Partial<Berichtsbeschreibung> {
  return Object.fromEntries(felder.map((f) => [f, (b[f] ?? []).filter((x) => x !== kennung)]));
}

/** Dazu die Anzeige: Eine fehlende ist dieselbe wie die Vorgabe (Fassung 1 bis 4). */
function gleich(a: Berichtsbeschreibung, b: Berichtsbeschreibung): boolean {
  return rechenkern(a) === rechenkern(b) && anzeigeGleich(a.anzeige, b.anzeige);
}

type Offen = 'speichern' | 'umbenennen' | 'loeschen' | 'teilen' | 'kopie' | 'export' | 'versand' | 'verwerfen' | 'neu' | null;

export function Baukasten({
  id,
  darfExportieren,
  darfTeilen,
  beiKopf,
  beiGespeichert,
  beiGeloescht,
  beiBeleg,
  beiNeu,
}: {
  /** Ein gespeicherter Bericht; ohne ihn ein neuer Entwurf. */
  readonly id?: string | undefined;
  readonly darfExportieren: boolean;
  /** AeA-42: mit allen teilen. Mit dem eigenen Team teilen darf jeder Eigentuemer. */
  readonly darfTeilen: boolean;
  /** AUS-07: Sprung in die Akte eines Belegs; `undefined`, wenn es keine gibt. */
  readonly beiBeleg: (art: Belegart, id: string, nummer: string | null) => (() => void) | undefined;
  readonly beiKopf: (kopf: Kopf) => void;
  readonly beiGespeichert: (id: string) => void;
  readonly beiGeloescht: () => void;
  /** Auftrag CRM-47: aus einem gespeicherten Bericht einen neuen beginnen. */
  readonly beiNeu?: (() => void) | undefined;
}): JSX.Element {
  const [katalog, setzeKatalog] = useState<Kennzahlenkatalog | undefined>(undefined);
  const [gespeichert, setzeGespeichert] = useState<GespeicherterBericht | undefined>(undefined);
  const [beschreibung, setzeBeschreibung] = useState<Berichtsbeschreibung | undefined>(undefined);
  const [ergebnis, setzeErgebnis] = useState<Abfrageergebnis | undefined>(undefined);
  const [laedt, setzeLaedt] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [hinweis, setzeHinweis] = useState<string | undefined>(undefined);
  const [suche, setzeSuche] = useState('');
  const [offen, setzeOffen] = useState<Offen>(null);
  const [fehlend, setzeFehlend] = useState<readonly FehlendeKennzahl[]>([]);
  const [zelle, setzeZelle] = useState<Zelle | undefined>(undefined);
  /* Auftrag #54: der offene KI-Vorschlag, der Stand vor ihm und die KI-Funktionen des Anwenders. */
  const [kiVorschlag, setzeKiVorschlag] = useState<KiVorschlag<Berichtsentwurf> | undefined>(undefined);
  const [vorKi, setzeVorKi] = useState<Berichtsbeschreibung | undefined>(undefined);
  const [kiTitel, setzeKiTitel] = useState<string | undefined>(undefined);
  const [kiFunktionen, setzeKiFunktionen] = useState<ReadonlySet<string>>(new Set());

  /* KI-07: Ohne KI — aus, nicht eingerichtet, nicht erreichbar — geht alles wie bisher. */
  useEffect(() => {
    let aktuell = true;
    void kiStand()
      .then((s) => {
        if (aktuell) setzeKiFunktionen(new Set(s.filter((f) => f.verfuegbarkeit.verfuegbar).map((f) => f.funktion)));
      })
      .catch(() => undefined);
    return () => {
      aktuell = false;
    };
  }, []);

  /* ── Laden: Katalog und, wenn es einen gibt, der gespeicherte Bericht ── */
  /* Auftrag CRM-165: drei Stufen statt „wird geladen"; ein neuer Bericht laedt neu und bricht den alten ab. */
  const geladen = useLadestufe(
    (signal) =>
      Promise.all([holeKatalog(signal), id === undefined ? Promise.resolve(undefined) : holeBericht(id, signal)]),
    id ?? '',
  );
  const ladefehler = geladen.fehler instanceof ApiFehler ? geladen.fehler : undefined;
  useEffect(() => {
    if (geladen.daten === undefined) return;
    const [k, b] = geladen.daten;
    setzeKatalog(k);
    setzeGespeichert(b);
    // Auftrag CRM-46: Ein neuer Bericht beginnt ohne Kennzahl; ein gespeicherter, wie er gespeichert ist.
    setzeBeschreibung(b?.beschreibung ?? vorgabe());
  }, [geladen.daten]);

  /* ── Rechnen: jede Aenderung der Beschreibung, die juengste gewinnt ─── */
  /* Auftrag CRM-47: Die Anzeige rechnet nicht — wer die Legende abschaltet, loest keine Abfrage aus. */
  const kern = beschreibung === undefined ? undefined : rechenkern(beschreibung);
  const lauf = useRef(0);
  useEffect(() => {
    if (beschreibung === undefined) return;
    if (beschreibung.kennzahlen.length === 0) {
      // Auftrag CRM-46: ohne Kennzahl nichts rechnen — und kein altes Ergebnis stehen lassen.
      lauf.current += 1;
      setzeErgebnis(undefined);
      setzeLaedt(false);
      return;
    }
    const nummer = ++lauf.current;
    setzeLaedt(true);
    /*
     * Der gespeicherte Bericht, unverstellt: mit den Rechten des Betrachters
     * und der Auskunft, welche Kennzahl ihm fehlt (Auftrag #27, AUS-06).
     */
    const unverstellt = gespeichert !== undefined && gleich(gespeichert.beschreibung, beschreibung);
    // Kurz warten: Wer zwei Filter hintereinander setzt, will EIN Ergebnis.
    const takt = setTimeout(() => {
      const rechnen = unverstellt
        ? berichtErgebnis(gespeichert.id).then((r) => ({ e: r.ergebnis, fehlt: r.fehlend }))
        : abfrage(beschreibung).then((e) => ({ e, fehlt: [] as readonly FehlendeKennzahl[] }));
      void rechnen
        .then(({ e, fehlt }) => {
          if (nummer !== lauf.current) return;
          setzeErgebnis(e ?? undefined);
          setzeFehlend(fehlt);
          setzeFehler(undefined);
        })
        .catch((u: unknown) => {
          if (nummer === lauf.current && u instanceof ApiFehler) setzeFehler(u);
        })
        .finally(() => {
          if (nummer === lauf.current) setzeLaedt(false);
        });
    }, 200);
    return () => clearTimeout(takt);
    // `gespeichert` wechselt nur mit dem Laden und Speichern — dann soll neu gerechnet werden.
  }, [kern, gespeichert]);

  const gewaehlt =
    katalog === undefined || beschreibung === undefined ? [] : gewaehlteKennzahlen(katalog.kennzahlen, beschreibung);
  const gruppierung = katalog?.gruppierungen.find((g) => g.kennung === beschreibung?.gruppierung);
  const aufteilung = katalog?.gruppierungen.find((g) => g.kennung === beschreibung?.aufteilung);
  const geaendert =
    gespeichert !== undefined && beschreibung !== undefined && !gleich(gespeichert.beschreibung, beschreibung);
  const titel = gespeichert?.titel ?? kiTitel ?? vorschlagstitel(gewaehlt, gruppierung, aufteilung);
  /* Ohne Kennzahl gibt es nichts zu speichern und nichts zu exportieren (Auftrag CRM-46). */
  const leer = beschreibung === undefined || beschreibung.kennzahlen.length === 0;
  /* Auftrag CRM-47: angefangen — neu und nicht mehr die leere Vorauswahl, oder gegenueber dem Gespeicherten verstellt. */
  const angefangen =
    beschreibung !== undefined && (gespeichert === undefined ? !gleich(beschreibung, vorgabe()) : geaendert);

  const aendere = useCallback((teil: Partial<Berichtsbeschreibung>): void => {
    setzeHinweis(undefined);
    setzeBeschreibung((b) => (b === undefined ? b : { ...b, ...teil }));
  }, []);

  /* ── Handlungen ─────────────────────────────────────────────────────── */
  /*
   * AUS-08: CSV, XLSX oder PDF (Auftrag #28) — der Server rechnet mit den Rechten des Ausfuehrenden.
   * Das PDF oeffnet gleich im Betrachter (Auftrag CRM-168); dafuer steht `ladeUndReiche` vor dem ersten `await`.
   */
  const herunterladen = useCallback(async (format: Exportformat, mitBelegen: boolean): Promise<void> => {
    if (beschreibung === undefined) return;
    try {
      let name = '';
      const gesperrt = await ladeUndReiche(async () => {
        const antwort = await exportDatei(format, gespeichert?.titel ?? null, beschreibung, mitBelegen);
        name = antwort.name;
        return { blob: antwort.datei, dateiname: antwort.name };
      }, format === 'pdf');
      setzeHinweis(gesperrt === null ? `Die Datei ${name} ist erstellt.` : `Die Datei ${name} ist erstellt. ${gesperrt}`);
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  }, [beschreibung, gespeichert]);

  /* KI-04: Wer mit offenem Vorschlag speichert, uebernimmt ihn — so, wie er jetzt dasteht. */
  const kiAbschliessen = useCallback(async (): Promise<void> => {
    if (kiVorschlag === undefined || beschreibung === undefined) return;
    try {
      await berichtsvorschlagEntscheiden(
        kiVorschlag.id,
        kiVorschlag.entwurf.beschreibung === null ? { art: 'verwerfen' } : { art: 'uebernehmen', beschreibung },
      );
    } catch (u) {
      // Schon entschieden (zweites Fenster): gespeichert wird trotzdem.
      if (!(u instanceof ApiFehler)) throw u;
    }
    setzeKiVorschlag(undefined);
    setzeVorKi(undefined);
  }, [kiVorschlag, beschreibung]);

  /*
   * Auftrag CRM-47: zuruecksetzen — ein neuer Bericht auf die leere
   * Vorauswahl, ein gespeicherter auf seinen gespeicherten Stand. Der Fokus
   * geht an die Suche nach Kennzahlen, wo man neu anfaengt; der Knopf, der
   * die Rueckfrage oeffnete, ist danach weg.
   */
  const verwerfen = useCallback((): void => {
    setzeOffen(null);
    setzeKiTitel(undefined);
    setzeZelle(undefined);
    setzeFehler(undefined);
    setzeBeschreibung(gespeichert?.beschreibung ?? vorgabe());
    setzeHinweis(
      gespeichert === undefined
        ? 'Der Entwurf ist verworfen. Fügen Sie über „Kennzahl hinzufügen“ eine Kennzahl hinzu, um neu anzufangen.'
        : 'Die Änderungen sind verworfen; der Bericht steht wieder, wie er gespeichert ist.',
    );
    setTimeout(() => document.getElementById('bk-kennzahl-hinzu')?.focus(), 0);
  }, [gespeichert]);

  const beiKiVorschlag = (v: KiVorschlag<Berichtsentwurf>): void => {
    if (kiVorschlag === undefined) setzeVorKi(beschreibung);
    setzeKiVorschlag(v);
    setzeHinweis(undefined);
    if (v.entwurf.beschreibung !== null) {
      // Auftrag CRM-47: „In eigenen Worten" laesst die Anzeige, wie sie ist.
      const anzeige = beschreibung?.anzeige;
      setzeBeschreibung({ ...v.entwurf.beschreibung, ...(anzeige === undefined ? {} : { anzeige }) });
      setzeKiTitel(v.entwurf.titel);
    }
  };

  const beiKiEntschieden = (v: KiVorschlag<Berichtsentwurf>): void => {
    setzeKiVorschlag(undefined);
    setzeVorKi(undefined);
    if (v.entscheidung !== 'verworfen') {
      setzeHinweis(
        `Der KI-Vorschlag ist ${v.entscheidung === 'geaendert' ? 'mit Ihren Änderungen ' : ''}übernommen. Speichern Sie den Bericht, wenn Sie ihn behalten wollen.`,
      );
      return;
    }
    if (v.entwurf.beschreibung === null) return;
    if (vorKi !== undefined) setzeBeschreibung(vorKi);
    setzeKiTitel(undefined);
    setzeHinweis('Der KI-Vorschlag ist verworfen; der Baukasten steht wieder wie vorher.');
  };

  const speichernOhneDialog = useCallback(async (): Promise<void> => {
    if (gespeichert === undefined || beschreibung === undefined) return;
    try {
      await kiAbschliessen();
      const neu = await berichtAendern(gespeichert.id, { beschreibung });
      setzeGespeichert(neu);
      setzeHinweis('Der Bericht ist gespeichert.');
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  }, [gespeichert, beschreibung, kiAbschliessen]);

  /* Die Handlungen stehen im Seitenkopf (ÄA-41) — dort, wo man sie sucht. */
  /* Auftrag #27: den Bericht als Widget ans Ende des eigenen Arbeitsplatzes legen. */
  const aufsDashboard = useCallback(async (): Promise<void> => {
    if (gespeichert === undefined) return;
    try {
      const jetzt = await holeAnordnung();
      const widget = berichtswidget(gespeichert.id);
      if (jetzt.felder.some((f) => f.kennung === widget)) {
        setzeHinweis('Der Bericht liegt schon auf Ihrem Dashboard.');
        return;
      }
      /* Auftrag #35: in der Vorgabegröße an die erste freie Stelle des Rasters. */
      const groesse = jetzt.verfuegbar.find((w) => w.kennung === widget)?.vorgabe ?? vorgabegroesse(widget);
      await anordnen([...jetzt.felder, { kennung: widget, ...groesse, ...ersteFreieStelle(jetzt.felder, groesse) }]);
      setzeHinweis('Der Bericht liegt jetzt auf Ihrem Dashboard, an der ersten freien Stelle.');
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  }, [gespeichert]);

  const rueckrufe = useRef({ herunterladen, speichernOhneDialog, aufsDashboard });
  rueckrufe.current = { herunterladen, speichernOhneDialog, aufsDashboard };
  /* Fuer andere ist ein geteilter Bericht nur lesbar (AUS-16). */
  const eigen = gespeichert === undefined || gespeichert.eigen;
  useEffect(() => {
    beiKopf({
      kicker: kiVorschlag !== undefined ? 'Bericht · KI-Vorschlag' : gespeichert === undefined ? 'Bericht · Entwurf' : 'Bericht',
      titel,
      unterzeile:
        gespeichert === undefined
          ? 'Noch nicht gespeichert'
          : !eigen
            ? `Geteilt von ${gespeichert.eigentuemer} · nur lesbar${geaendert ? ' · verstellt, nicht gespeichert' : ''}`
            : geaendert
              ? 'Ungespeicherte Änderungen'
              : `Gespeichert · geändert ${zeitpunktText(gespeichert.geaendertAm)}`,
      sinnbild: 'chart-bar:light',
      kennzeichen:
        gespeichert === undefined ? undefined : <span className="marke">{FREIGABENAMEN[gespeichert.freigabe]}</span>,
      handlungen: (
        <>
          {darfExportieren && (
            <button
              type="button"
              className="knopf"
              disabled={leer}
              onClick={() => setzeOffen('export')}
            >
              <Sinnbild gewicht="light" name="download-simple" />
              Exportieren
            </button>
          )}
          {gespeichert !== undefined && (
            <button type="button" className="knopf" onClick={() => void rueckrufe.current.aufsDashboard()}>
              <Sinnbild gewicht="light" name="squares-four" />
              Aufs Dashboard
            </button>
          )}
          {/* AUS-15 (Auftrag #30): der Versand nach Zeitplan — nur fuer den Eigentuemer. */}
          {gespeichert !== undefined && eigen && (
            <button type="button" className="knopf" onClick={() => setzeOffen('versand')}>
              <Sinnbild gewicht="light" name="paper-plane-tilt" />
              Versand
            </button>
          )}
          {gespeichert !== undefined && eigen && (
            <button type="button" className="knopf" onClick={() => setzeOffen('teilen')}>
              <Sinnbild gewicht="light" name="share-network" />
              Teilen
            </button>
          )}
          {gespeichert !== undefined && eigen && (
            <>
              <button type="button" className="knopf" onClick={() => setzeOffen('umbenennen')}>
                Umbenennen
              </button>
              <button type="button" className="knopf knopf--eingriff" onClick={() => setzeOffen('loeschen')}>
                <Sinnbild gewicht="light" name="trash" />
                Löschen …
              </button>
            </>
          )}
          {/* Auftrag CRM-47: Verwerfen fragt nach; der KI-Vorschlag hat sein eigenes (KI-04). */}
          {angefangen && kiVorschlag === undefined && (
            <button type="button" className="knopf knopf--eingriff" onClick={() => setzeOffen('verwerfen')}>
              <Sinnbild gewicht="light" name="arrow-u-up-left" />
              Verwerfen …
            </button>
          )}
          {gespeichert !== undefined && beiNeu !== undefined && (
            <button type="button" className="knopf" onClick={() => (geaendert ? setzeOffen('neu') : beiNeu())}>
              <Sinnbild gewicht="light" name="plus" />
              Neuer Bericht
            </button>
          )}
          {eigen ? (
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={leer || (gespeichert !== undefined && !geaendert)}
              onClick={() =>
                gespeichert === undefined ? setzeOffen('speichern') : void rueckrufe.current.speichernOhneDialog()
              }
            >
              Speichern
            </button>
          ) : (
            <button type="button" className="knopf knopf--haupt" onClick={() => setzeOffen('kopie')}>
              Als eigenen Bericht speichern
            </button>
          )}
        </>
      ),
    });
  }, [beiKopf, titel, gespeichert, geaendert, darfExportieren, beschreibung, eigen, kiVorschlag, leer, angefangen, beiNeu]);

  if (katalog === undefined || beschreibung === undefined) {
    /* Bis die Antwort in den Zustand uebernommen ist, steht weiter das Skelett (kein leeres Bild). */
    const anzeigefehler = ladefehler ?? fehler;
    return (
      <>
        {anzeigefehler !== undefined && <Meldung fehler={anzeigefehler} />}
        <Ladeanzeige zustand={geladen} was="Der Baukasten" skelett={<Baukastenskelett />}>
          <Baukastenskelett />
        </Ladeanzeige>
      </>
    );
  }

  if (bereiteKennzahlen(katalog.kennzahlen).length === 0) {
    return (
      <p className="hilfe">Ihnen fehlt das Recht an jeder Kennzahl.</p>
    );
  }

  return (
    <div className="baukasten">
      <Bausteine
        katalog={katalog}
        beschreibung={beschreibung}
        suche={suche}
        beiSuche={setzeSuche}
        /* Ans Ende: Jede Kennzahl behaelt ihre Stelle und damit ihre Farbe (Auftrag CRM-42). */
        beiHinzu={(k) => aendere({ kennzahlen: [...beschreibung.kennzahlen, k.kennung] })}
        beiEntfernen={(kennung) => {
          const rest = beschreibung.kennzahlen.filter((x) => x !== kennung);
          // Auftrag CRM-86, CRM-88: Min und Max und die Optionen gehen mit ihrer Kennzahl.
          if (rest.length > 0) aendere({ kennzahlen: rest, ...ohneOptionen(beschreibung, kennung, OPTIONSLISTEN) });
        }}
        /* AUS-16 (Auftrag CRM-86) und ÄA-53 (Auftrag CRM-88): Min und Max und die Optionen je Kennzahl. */
        beiOption={(feld, kennung, an) => {
          // AUS-31: Ohne Median keine Quartile.
          const mit = feld === 'median' && !an ? (['median', 'quartile'] as const) : ([feld] as const);
          const ohne = ohneOptionen(beschreibung, kennung, mit);
          aendere(an ? { ...ohne, [feld]: [...(ohne[feld] ?? []), kennung] } : ohne);
        }}
        beiBerichtsoption={aendere}
        /*
          Auftrag CRM-46: Ohne Gruppierung oder auf die Aufteilung gruppiert faellt die Aufteilung weg.
          AUS-33 (Auftrag CRM-88): ohne Zeitachse ebenso kumuliert — der Server wiese es ab.
        */
        beiGruppierung={(g) =>
          aendere({
            gruppierung: g,
            ...(g === null || g === beschreibung.aufteilung ? { aufteilung: null } : {}),
            ...(katalog.gruppierungen.find((x) => x.kennung === g)?.zeitlich === true ? {} : { kumuliert: [] }),
          })
        }
        beiAufteilung={(t) => aendere({ aufteilung: t })}
      />

      <section className="karte baukasten__ergebnis" aria-labelledby="bk-ergebnis" aria-busy={laedt}>
        <div className="karte__titel">
          <h2 id="bk-ergebnis">Ergebnis</h2>
          <small role="status">
            {laedt
              ? 'wird gerechnet …'
              : ergebnis === undefined
                ? ''
                : `Stand ${new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(ergebnis.stand))}`}
          </small>
        </div>
        {kiFunktionen.has('bericht_interaktiv') && (
          <InEigenenWorten
            beschreibung={beschreibung}
            vorschlag={kiVorschlag}
            beiVorschlag={beiKiVorschlag}
            beiEntschieden={beiKiEntschieden}
          />
        )}
        {fehler !== undefined && <Meldung fehler={fehler} />}
        {hinweis !== undefined && (
          <p className="hinweiszeile" role="status">
            {hinweis}
          </p>
        )}
        {fehlend.length > 0 && (
          <p className="hinweiszeile" role="status">
            Für {fehlend.map((k) => `„${k.name}"`).join(' und ')} fehlt Ihnen das Recht (
            {fehlend.map((k) => k.recht).join(', ')}){ergebnis === undefined ? ' — der Bericht rechnet nichts.' : '.'}
          </p>
        )}
        {beschreibung.kennzahlen.length === 0 && (
          <p className="hilfe baukasten__leer" role="status">
            Fügen Sie eine Kennzahl hinzu.
          </p>
        )}
        {ergebnis !== undefined && beschreibung.kennzahlen.length > 0 && (
          <Ergebnisansicht
            ergebnis={ergebnis}
            anzeige={anzeigeVon(beschreibung)}
            darstellung={beschreibung.darstellung}
            beiDarstellung={(d) => aendere(mitOptionen(beschreibung, d, ergebnis))}
            beiKlassen={aendere}
            beiZelle={setzeZelle}
          />
        )}
        {/* Eine neue Beschreibung ist ein neuer Bericht: Die Erlaeuterung beginnt von vorn. */}
        {ergebnis !== undefined && kiFunktionen.has('bericht_erlaeuterung') && (
          <Erlaeuterung key={rechenkern(ergebnis.beschreibung)} beschreibung={ergebnis.beschreibung} />
        )}
      </section>

      <Einstellungen
        katalog={katalog}
        gewaehlt={gewaehlt}
        beschreibung={beschreibung}
        ergebnis={ergebnis}
        beiZeitraum={(z) => aendere({ zeitraum: z })}
        beiFilter={(f) => aendere({ filter: f })}
        beiVergleichen={(v) => aendere({ vergleiche: v })}
        beiAnzeige={(a) => aendere({ anzeige: a })}
      />

      {offen === 'speichern' && (
        <Titeldialog
          titel="Bericht speichern"
          unterzeile="Privat — nur Sie sehen ihn."
          vorgabe={titel}
          knopf="Speichern"
          beiSchliessen={() => setzeOffen(null)}
          beiTitel={async (t) => {
            await kiAbschliessen();
            const neu = await berichtAnlegen({ titel: t, beschreibung });
            setzeOffen(null);
            beiGespeichert(neu.id);
          }}
        />
      )}
      {offen === 'kopie' && gespeichert !== undefined && (
        <Titeldialog
          titel="Als eigenen Bericht speichern"
          unterzeile="Die Kopie ist privat; das Original bleibt."
          vorgabe={`${gespeichert.titel} (Kopie)`}
          knopf="Speichern"
          beiSchliessen={() => setzeOffen(null)}
          beiTitel={async (t) => {
            const neu = await berichtAnlegen({ titel: t.slice(0, 120), beschreibung });
            setzeOffen(null);
            beiGespeichert(neu.id);
          }}
        />
      )}
      {offen === 'teilen' && gespeichert !== undefined && (
        <Teilendialog
          freigabe={gespeichert.freigabe}
          darfAlle={darfTeilen}
          beiSchliessen={() => setzeOffen(null)}
          beiFreigabe={async (f) => {
            setzeGespeichert(await berichtAendern(gespeichert.id, { freigabe: f }));
            setzeOffen(null);
            setzeHinweis(
              f === 'privat'
                ? 'Der Bericht ist wieder privat.'
                : `Der Bericht ist mit ${f === 'team' ? 'Ihrem Team' : 'allen, die Berichte sehen,'} geteilt. Jeder sieht darin seine eigenen Zahlen.`,
            );
          }}
        />
      )}
      {offen === 'versand' && gespeichert !== undefined && (
        <Versanddialog bericht={gespeichert.id} beiSchliessen={() => setzeOffen(null)} />
      )}
      {offen === 'export' && (
        <Exportdialog
          beiSchliessen={() => setzeOffen(null)}
          beiExport={async (format, mitBelegen) => {
            await herunterladen(format, mitBelegen);
            setzeOffen(null);
          }}
        />
      )}
      {zelle !== undefined && ergebnis !== undefined && (
        <Herkunft
          beschreibung={ergebnis.beschreibung}
          zelle={zelle}
          beiBeleg={beiBeleg}
          beiSchliessen={() => setzeZelle(undefined)}
        />
      )}
      {(offen === 'verwerfen' || offen === 'neu') && (
        <Rueckfrage
          frage={offen === 'neu' ? 'Neuen Bericht beginnen?' : 'Bericht verwerfen?'}
          unterzeile={titel}
          tat={offen === 'neu' ? 'Verwerfen und neu beginnen' : 'Verwerfen'}
          beiSchliessen={() => setzeOffen(null)}
          beiBestaetigen={() => {
            if (offen === 'verwerfen') {
              verwerfen();
              return;
            }
            setzeOffen(null);
            beiNeu?.();
          }}
        >
          <p>
            {gespeichert === undefined
              ? 'Der nicht gespeicherte Entwurf geht verloren.'
              : 'Die Änderungen gehen verloren; der gespeicherte Bericht bleibt, wie er ist.'}
          </p>
        </Rueckfrage>
      )}
      {offen === 'umbenennen' && gespeichert !== undefined && (
        <Titeldialog
          titel="Bericht umbenennen"
          vorgabe={gespeichert.titel}
          knopf="Umbenennen"
          beiSchliessen={() => setzeOffen(null)}
          beiTitel={async (t) => {
            setzeGespeichert(await berichtAendern(gespeichert.id, { titel: t }));
            setzeOffen(null);
          }}
        />
      )}
      {offen === 'loeschen' && gespeichert !== undefined && (
        <Rueckfrage
          frage="Bericht löschen?"
          unterzeile={gespeichert.titel}
          tat="Löschen"
          beiSchliessen={() => setzeOffen(null)}
          beiBestaetigen={async () => {
            await berichtLoeschen(gespeichert.id);
            setzeOffen(null);
            beiGeloescht();
          }}
        >
          <p>Gelöscht wird nur die Zusammenstellung, nicht die Zahlen.</p>
        </Rueckfrage>
      )}
    </div>
  );
}

/* ── Links: die Bausteine ────────────────────────────────────────────────── */

/** Welches Pop-up der Bausteine offen ist (Auftrag CRM-54). */
type Bausteinwahl = 'kennzahl' | 'gruppierung' | 'aufteilung';

/** Stufe 1 des Baukastens (Auftrag CRM-165): Bausteine links, das Diagramm daneben. */
function Baukastenskelett(): JSX.Element {
  return (
    <section className="karte">
      <Skelett art="liste" />
      <Skelett art="verlauf" />
    </section>
  );
}

/** Das Zeichen einer Kennzahl nach ihrer Einheit — in der Spalte und im Pop-up dasselbe. */
function kennzahlzeichen(k: Katalogkennzahl): 'coins' | 'chart-line-up' | 'chart-bar' {
  return k.einheit === 'EUR' ? 'coins' : k.einheit === 'Prozent' || k.einheit === 'Tage' || k.einheit === 'Stunden' ? 'chart-line-up' : 'chart-bar';
}

/**
 * DIE BAUSTEINE: NUR DAS GEWÄHLTE, DER REST IM POP-UP (Auftrag CRM-54).
 *
 * Die Spalte zeigt je Abschnitt (Kennzahlen, Gruppieren nach, Aufteilen
 * nach) nur, was im Bericht steht — je Eintrag mit Entfernen — und darunter
 * einen Knopf. Er öffnet ein Pop-up (`Dialog`: Fokus hinein und zurück,
 * Tabulator im Kreis, Escape schließt) mit der Suche und der vollständigen
 * Liste samt Bereichen, Zeichen, gesperrten vorbereiteten Kennzahlen mit
 * Grund (CRM-44) und den Regeln, was zusammenpasst (CRM-42, CRM-46). Ohne die
 * langen Listen passt die Spalte schmal neben das Ergebnis (dreispaltig ab
 * 1024 px).
 *
 * GRUPPIERUNG UND AUFTEILUNG schließen das Pop-up mit der Wahl: Es gibt je
 * genau eine. KENNZAHLEN lassen es offen — ein Bericht hat oft mehrere, und
 * jede Wahl ändert, was noch passt (Einheit und Achse, Gruppierung). Im
 * offenen Pop-up sieht man das sofort an den ausgegrauten Einträgen samt
 * Grund und wählt die nächste, statt für jede den Knopf neu zu suchen; ein
 * zweiter Druck nimmt eine Kennzahl wieder heraus, „Fertig" oder Escape
 * schließt.
 */
function Bausteine({
  katalog,
  beschreibung,
  suche,
  beiSuche,
  beiHinzu,
  beiEntfernen,
  beiGruppierung,
  beiAufteilung,
  beiOption,
  beiBerichtsoption,
}: {
  readonly katalog: Kennzahlenkatalog;
  readonly beschreibung: Berichtsbeschreibung;
  readonly suche: string;
  readonly beiSuche: (s: string) => void;
  readonly beiHinzu: (k: Katalogkennzahl) => void;
  readonly beiEntfernen: (kennung: string) => void;
  readonly beiGruppierung: (g: Gruppierungskennung | null) => void;
  readonly beiAufteilung: (g: Gruppierungskennung | null) => void;
  readonly beiOption: (feld: Optionsliste, kennung: string, an: boolean) => void;
  readonly beiBerichtsoption: (aenderung: Pick<Berichtsbeschreibung, 'anteilAn' | 'kumuliertAls'>) => void;
}): JSX.Element {
  const [offen, setzeOffen] = useState<Bausteinwahl | null>(null);
  const gewaehlt = gewaehlteKennzahlen(katalog.kennzahlen, beschreibung);
  const { seiten, zweiAchsen } = achsenDerAuswahl(gewaehlt);
  const einzige = gewaehlt.length === 1;
  const gruppierung = katalog.gruppierungen.find((g) => g.kennung === beschreibung.gruppierung);
  const aufteilung = katalog.gruppierungen.find((g) => g.kennung === beschreibung.aufteilung);

  return (
    <section className="karte baukasten__bausteine" aria-labelledby="bk-bausteine">
      <div className="karte__titel">
        <h2 id="bk-bausteine">Bausteine</h2>
      </div>

      {/* Die gewaehlten Kennzahlen: die letzte laesst sich nicht entfernen (Auftrag CRM-42). */}
      <p className="k baustein__gruppe" id="bk-gewaehlt">
        Kennzahlen im Bericht
      </p>
      {gewaehlt.length > 0 && (
        <>
          <ul className="kennzahlwahl" aria-labelledby="bk-gewaehlt">
            {gewaehlt.map((k, i) => (
              <li key={k.kennung} className="kennzahlwahl__eintrag kennzahlwahl__eintrag--kennzahl">
                <span className="kennzahlwahl__farbe" data-reihe={i + 1} aria-hidden="true" />
                <span className="kennzahlwahl__name">
                  {k.name}
                  <small>
                    {einheitText(k.einheit)} · {ACHSENNAMEN[seiten[i]!]}
                  </small>
                </span>
                <span className="kennzahlwahl__knoepfe">
                  <Kennzahlerklaerung k={k} achse={zweiAchsen ? ACHSENNAMEN[seiten[i]!] : null} />
                  <button
                    type="button"
                    className="knopf knopf--klein kennzahlwahl__entfernen"
                    aria-label={`„${k.name}“ entfernen`}
                    title={einzige ? 'Ein Bericht braucht mindestens eine Kennzahl.' : `„${k.name}“ entfernen`}
                    disabled={einzige}
                    onClick={() => beiEntfernen(k.kennung)}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </span>
                {/*
                  Auftrag CRM-107: Min/Max und die Optionen in EINEM Raster gleich
                  breiter Spalten, bündig unter dem Namen — gesperrte gleich breit
                  und gedimmt, nichts verschiebt sich.
                */}
                {(k.minMax !== undefined || k.optionen !== undefined) && (
                  <span className="kennzahlwahl__optionen">
                    <MinMaxWahl
                      k={k}
                      an={(beschreibung.minMax ?? []).includes(k.kennung)}
                      beiWahl={(an) => beiOption('minMax', k.kennung, an)}
                    />
                    <Kennzahloptionen
                      k={k}
                      beschreibung={beschreibung}
                      zeitachse={gruppierung?.zeitlich === true}
                      beiWahl={(feld, an) => beiOption(feld, k.kennung, an)}
                    />
                  </span>
                )}
              </li>
            ))}
          </ul>
          {!zweiAchsen && <p className="feld__hilfe">Eine andere Einheit kommt auf eine zweite Achse.</p>}
          {/* Auftrag CRM-101: Die Erklaerungen haengen nur noch per aria-describedby an den Schaltern; sichtbar ist die Kurzinfo. */}
          <p hidden id="bk-minmax-hilfe">
            {MINMAX_HILFE}
          </p>
          <p hidden id="bk-optionen-hilfe">
            {OPTIONEN_HILFE}
          </p>
          <Berichtsoptionen
            beschreibung={beschreibung}
            aufgeteilt={aufteilung !== undefined}
            beiWahl={beiBerichtsoption}
          />
        </>
      )}
      <button
        type="button"
        id="bk-kennzahl-hinzu"
        className="knopf baukasten__hinzu"
        aria-haspopup="dialog"
        onClick={() => setzeOffen('kennzahl')}
      >
        <Sinnbild gewicht="light" name="plus" />
        Kennzahl hinzufügen
      </button>

      <p className="k baustein__gruppe" id="bk-gruppierung">
        Gruppieren nach
      </p>
      {gruppierung !== undefined && (
        <ul className="kennzahlwahl" aria-labelledby="bk-gruppierung">
          <Gewaehlt
            name={gruppierung.name}
            zeichen={gruppierung.zeitlich ? 'calendar-blank' : 'stack'}
            beiEntfernen={() => beiGruppierung(null)}
          />
        </ul>
      )}
      <button type="button" className="knopf baukasten__hinzu" aria-haspopup="dialog" onClick={() => setzeOffen('gruppierung')}>
        <Sinnbild gewicht="light" name="plus" />
        Gruppierung wählen
      </button>

      {/*
        Auftrag CRM-46: die zweite Dimension. Je Wert eine Reihe im Bild und
        eine Spalte in der Tabelle — „Teilnehmer je Monat, aufgeteilt nach
        Kundenart".
      */}
      <p className="k baustein__gruppe" id="bk-aufteilung">
        Aufteilen nach
      </p>
      {aufteilung !== undefined && (
        <ul className="kennzahlwahl" aria-labelledby="bk-aufteilung">
          <Gewaehlt name={aufteilung.name} zeichen="stack" beiEntfernen={() => beiAufteilung(null)} />
        </ul>
      )}
      <button type="button" className="knopf baukasten__hinzu" aria-haspopup="dialog" onClick={() => setzeOffen('aufteilung')}>
        <Sinnbild gewicht="light" name="plus" />
        Aufteilung wählen
      </button>

      {offen === 'kennzahl' && (
        <Kennzahlwahl
          katalog={katalog}
          beschreibung={beschreibung}
          gewaehlt={gewaehlt}
          suche={suche}
          beiSuche={beiSuche}
          beiHinzu={beiHinzu}
          beiEntfernen={beiEntfernen}
          beiSchliessen={() => setzeOffen(null)}
        />
      )}
      {offen === 'gruppierung' && (
        <Gruppenwahl
          art="gruppierung"
          katalog={katalog}
          beschreibung={beschreibung}
          gewaehlt={gewaehlt}
          beiWahl={(g) => {
            beiGruppierung(g);
            setzeOffen(null);
          }}
          beiSchliessen={() => setzeOffen(null)}
        />
      )}
      {offen === 'aufteilung' && (
        <Gruppenwahl
          art="aufteilung"
          katalog={katalog}
          beschreibung={beschreibung}
          gewaehlt={gewaehlt}
          beiWahl={(g) => {
            beiAufteilung(g);
            setzeOffen(null);
          }}
          beiSchliessen={() => setzeOffen(null)}
        />
      )}
    </section>
  );
}

/**
 * AUS-16 (Auftrag CRM-86): was der Wert je Gruppe ist und was Min und Max
 * daneben zeigen — fuer alle Kennzahlen; die eigene Ebene nennt der Schalter.
 */
const MINMAX_HILFE = '„Min/Max“ zeigt den kleinsten und den größten Einzelwert der Gruppe.';

/**
 * Der Schalter „Min/Max" einer gewählten Kennzahl (AUS-16, Auftrag CRM-86).
 * Wo es keinen Einzelwert gibt — Anzahl, Quote, Spitze, Reichweite — ist er
 * gesperrt und nennt den Grund wie ein gesperrter Baustein. Der Titel sagt,
 * was der Wert je Gruppe dieser Kennzahl ist.
 */
function MinMaxWahl({
  k,
  an,
  beiWahl,
}: {
  readonly k: Katalogkennzahl;
  readonly an: boolean;
  readonly beiWahl: (an: boolean) => void;
}): JSX.Element | null {
  const angabe = k.minMax;
  if (angabe === undefined) return null;
  const grundId = `bk-minmax-grund-${k.kennung}`;
  const name = `Min/Max ${angabe.moeglich ? angabe.je : 'anzeigen'}`;
  /* Auftrag CRM-107: sichtbar kurz wie die übrigen Optionen; „je Rechnung" nennen Name und Kurzinfo. */
  return (
    <>
      <label
        className={angabe.moeglich ? 'kennzahlwahl__option' : 'kennzahlwahl__option kennzahlwahl__option--gesperrt'}
        title={angabe.moeglich ? `${name}: ${k.gruppenwert ?? MINMAX_HILFE}` : angabe.grund}
      >
        <input
          type="checkbox"
          checked={angabe.moeglich && an}
          disabled={!angabe.moeglich}
          aria-label={name}
          aria-describedby={angabe.moeglich ? 'bk-minmax-hilfe' : `bk-minmax-hilfe ${grundId}`}
          onChange={(e) => beiWahl(e.currentTarget.checked)}
        />
        <span>Min/Max</span>
      </label>
      {/* Auftrag CRM-101: Der Grund steht in der Kurzinfo; fuer die Vorlesehilfe haengt er hier. */}
      {!angabe.moeglich && (
        <small hidden id={grundId}>
          {angabe.grund}
        </small>
      )}
    </>
  );
}

/**
 * ÄA-53 (Auftrag CRM-88): was die Optionen neben dem Wert zeigen — ein Satz
 * je Option, fuer alle Kennzahlen; der Grund einer Sperre steht am Schalter.
 */
const OPTIONEN_HILFE = 'Anteil am Gesamt, Median und Quartile der Einzelwerte, n ihre Anzahl, kumuliert die laufende Summe.';

/** Die Schalter einer Option; `feld` ist der Name in der Beschreibung. */
type Optionsschalter = {
  readonly feld: 'anteil' | 'median' | 'quartile' | 'n' | 'kumuliert';
  readonly text: string;
  readonly hilfe: string;
};
/* AUS-30 Anteil, AUS-31 Median und Quartile, AUS-32 n, AUS-33 kumuliert. */
const OPTIONSSCHALTER: readonly Optionsschalter[] = [
  { feld: 'anteil', text: 'Anteil', hilfe: 'Anteil am Gesamt in Prozent' },
  { feld: 'median', text: 'Median', hilfe: 'Median der Einzelwerte' },
  { feld: 'quartile', text: 'Quartile', hilfe: 'Erstes und drittes Quartil der Einzelwerte' },
  { feld: 'n', text: 'n', hilfe: 'Anzahl der Einzelwerte' },
  { feld: 'kumuliert', text: 'Kumuliert', hilfe: 'Laufende Summe entlang der Zeitachse' },
];

/**
 * Die Optionen einer gewählten Kennzahl (ÄA-53, Auftrag CRM-88) — gesperrt
 * mit dem Grund des Katalogs; Quartile ohne Median und kumuliert ohne
 * Zeitachse mit dem Satz, den auch der Server nennt.
 */
function Kennzahloptionen({
  k,
  beschreibung,
  zeitachse,
  beiWahl,
}: {
  readonly k: Katalogkennzahl;
  readonly beschreibung: Berichtsbeschreibung;
  readonly zeitachse: boolean;
  readonly beiWahl: (feld: Optionsschalter['feld'], an: boolean) => void;
}): JSX.Element | null {
  const o = k.optionen;
  if (o === undefined) return null;
  const gewaehlt = (feld: Optionsschalter['feld']): boolean => (beschreibung[feld] ?? []).includes(k.kennung);
  const sperre = (feld: Optionsschalter['feld']): string | null => {
    const angabe = o[feld === 'quartile' ? 'median' : feld];
    if (!angabe.moeglich) return angabe.grund;
    if (feld === 'quartile' && !gewaehlt('median')) return QUARTILE_NUR_MIT_MEDIAN;
    if (feld === 'kumuliert' && !zeitachse) return OHNE_ZEITACHSE;
    return null;
  };
  /* Gleiche Gruende einmal: Bei einer Quote sperren Anteil und kumuliert aus demselben Grund. */
  const gruende = [...new Set(OPTIONSSCHALTER.map((s) => sperre(s.feld)).filter((g): g is string => g !== null))];
  const grundId = (g: string): string => `bk-option-grund-${k.kennung}-${String(gruende.indexOf(g))}`;
  return (
    <>
      {OPTIONSSCHALTER.map((s) => {
        const g = sperre(s.feld);
        return (
          <label
            key={s.feld}
            className={g === null ? 'kennzahlwahl__option' : 'kennzahlwahl__option kennzahlwahl__option--gesperrt'}
            title={g ?? s.hilfe}
          >
            <input
              type="checkbox"
              checked={g === null && gewaehlt(s.feld)}
              disabled={g !== null}
              aria-label={`${s.text} für „${k.name}“`}
              aria-describedby={g === null ? 'bk-optionen-hilfe' : `bk-optionen-hilfe ${grundId(g)}`}
              onChange={(e) => beiWahl(s.feld, e.currentTarget.checked)}
            />
            <span>{s.text}</span>
          </label>
        );
      })}
      {/* Auftrag CRM-101: Die Gruende stehen in der Kurzinfo des Schalters; fuer die Vorlesehilfe haengen sie hier. */}
      {gruende.map((g) => (
        <small key={g} hidden id={grundId(g)}>
          {g}
        </small>
      ))}
    </>
  );
}

/**
 * Zwei Wahlen fuer den ganzen Bericht (ÄA-53): woran der Anteil in den
 * Zellen einer Aufteilung misst (AUS-30) und ob die laufende Summe neben
 * oder statt des Werts steht (AUS-33). Sie stehen nur da, wenn sie wirken.
 */
function Berichtsoptionen({
  beschreibung,
  aufgeteilt,
  beiWahl,
}: {
  readonly beschreibung: Berichtsbeschreibung;
  readonly aufgeteilt: boolean;
  readonly beiWahl: (aenderung: Pick<Berichtsbeschreibung, 'anteilAn' | 'kumuliertAls'>) => void;
}): JSX.Element | null {
  const anteil = aufgeteilt && (beschreibung.anteil ?? []).length > 0;
  const kumuliert = (beschreibung.kumuliert ?? []).length > 0;
  if (!anteil && !kumuliert) return null;
  return (
    <div className="baukasten__berichtsoptionen">
      {/* Auftrag CRM-107: je Wahl der Titel über den Segmenten, die Segmente volle Breite. */}
      {anteil && (
        <div className="baukasten__berichtsoption">
          <span className="baukasten__optionstitel">Anteil in den Zellen</span>
          <Segmente
            leicht
            beschriftung="Anteil in den Zellen der Aufteilung"
            gewaehlt={beschreibung.anteilAn ?? 'gesamt'}
            beiWahl={(anteilAn) => beiWahl({ anteilAn })}
            optionen={[
              { wert: 'gesamt', text: 'am Gesamt' },
              { wert: 'zeile', text: 'an der Zeile' },
            ]}
          />
        </div>
      )}
      {kumuliert && (
        <div className="baukasten__berichtsoption">
          <span className="baukasten__optionstitel">Kumuliert</span>
          <Segmente
            leicht
            beschriftung="Kumuliert"
            gewaehlt={beschreibung.kumuliertAls ?? 'zusaetzlich'}
            beiWahl={(kumuliertAls) => beiWahl({ kumuliertAls })}
            optionen={[
              { wert: 'zusaetzlich', text: 'zusätzlich zum Wert' },
              { wert: 'statt', text: 'statt des Werts' },
            ]}
          />
        </div>
      )}
    </div>
  );
}

/**
 * AUS-01: die Definition einer gewählten Kennzahl hinter einem i-Knopf
 * (Auftrag CRM-107). Bis dahin stand sie als langer Block unten in den
 * Einstellungen, getrennt von der Kennzahl, die sie erklärt. Die Kurzkarte
 * ist die des Seitenkopfs (`Titelkarte`): Kreuz, Esc und Tippen daneben
 * schließen; sie öffnet nur auf Verlangen.
 */
function Kennzahlerklaerung({
  k,
  achse,
}: {
  readonly k: Katalogkennzahl;
  /** Die Achse, wenn der Bericht zwei hat — sonst null. */
  readonly achse: string | null;
}): JSX.Element {
  return (
    <Titelkarte
      zeichen="info"
      nurAufVerlangen
      klasse="kennzahlkarte"
      karte={{
        beschriftung: `Erklärung zu „${k.name}“`,
        inhalt: (
          <>
            <h3 className="kennzahlkarte__titel">
              {k.name}
              {achse !== null && ` · ${achse}`}
            </h3>
            <p>{k.definition.zaehlt}</p>
            <p>
              <strong>Gezählt:</strong> {k.definition.wann}
            </p>
            <p>
              <strong>Nicht:</strong> {k.definition.nicht}
            </p>
            <p className="kennzahlkarte__quelle">Quelle: {k.quelle}</p>
          </>
        ),
      }}
    />
  );
}

/** Ein gewählter Eintrag in der Spalte: Zeichen, Name, Entfernen (Auftrag CRM-54). */
function Gewaehlt({
  name,
  zeichen,
  beiEntfernen,
}: {
  readonly name: string;
  readonly zeichen: 'calendar-blank' | 'stack';
  readonly beiEntfernen: () => void;
}): JSX.Element {
  return (
    <li className="kennzahlwahl__eintrag">
      <Sinnbild gewicht="light" name={zeichen} klasse="kennzahlwahl__zeichen" />
      <span className="kennzahlwahl__name">{name}</span>
      <button
        type="button"
        className="knopf knopf--klein kennzahlwahl__entfernen"
        aria-label={`„${name}“ entfernen`}
        title={`„${name}“ entfernen`}
        onClick={beiEntfernen}
      >
        <span aria-hidden="true">×</span>
      </button>
    </li>
  );
}

/** Die Suche im Pop-up — oben, damit der Fokus beim Öffnen dort landet. */
function Bausteinsuche({
  id,
  wert,
  text,
  beiAenderung,
}: {
  readonly id?: string;
  readonly wert: string;
  readonly text: string;
  readonly beiAenderung: (s: string) => void;
}): JSX.Element {
  return (
    <label className="filterleiste__suche">
      <Sinnbild gewicht="light" name="magnifying-glass" />
      <input
        className="liste__suche"
        type="search"
        {...(id === undefined ? {} : { id })}
        value={wert}
        placeholder={text}
        aria-label={text}
        onChange={(e) => beiAenderung(e.target.value)}
      />
    </label>
  );
}

/** Pop-up „Kennzahl hinzufügen": alle Kennzahlen nach Bereich, bleibt für mehrere offen. */
function Kennzahlwahl({
  katalog,
  beschreibung,
  gewaehlt,
  suche,
  beiSuche,
  beiHinzu,
  beiEntfernen,
  beiSchliessen,
}: {
  readonly katalog: Kennzahlenkatalog;
  readonly beschreibung: Berichtsbeschreibung;
  readonly gewaehlt: readonly Katalogkennzahl[];
  readonly suche: string;
  readonly beiSuche: (s: string) => void;
  readonly beiHinzu: (k: Katalogkennzahl) => void;
  readonly beiEntfernen: (kennung: string) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const gruppenname = (g: string): string => katalog.gruppierungen.find((x) => x.kennung === g)?.name ?? g;
  const gefunden = katalog.kennzahlen.filter((k) => kennzahlTrifft(k, suche));
  const bereiche = [...new Set(gefunden.map((k) => k.bereich))];
  const einzige = gewaehlt.length === 1;
  return (
    <Dialog
      titel="Kennzahl hinzufügen"
      unterzeile="Ein zweiter Druck nimmt eine heraus."
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      <div className="bausteinwahl">
        <Bausteinsuche id="bk-kennzahlsuche" wert={suche} text="Kennzahl suchen" beiAenderung={beiSuche} />
        <div className="bausteinwahl__liste">
          {bereiche.map((bereich) => (
            <div key={bereich} role="group" aria-labelledby={`bk-bereich-${bereich}`}>
              <p className="k baustein__gruppe" id={`bk-bereich-${bereich}`}>
                {BEREICHSNAMEN[bereich]}
              </p>
              {gefunden
                .filter((k) => k.bereich === bereich)
                .map((k) => {
                  const drin = beschreibung.kennzahlen.includes(k.kennung);
                  const grund = kennzahlgrund(k, beschreibung, gewaehlt, gruppenname);
                  const beschreibungId = `bk-beschreibung-${k.kennung}`;
                  return (
                    <button
                      key={k.kennung}
                      type="button"
                      className="baustein"
                      aria-pressed={drin}
                      aria-disabled={grund !== null || (drin && einzige)}
                      aria-describedby={grund === null ? beschreibungId : `${beschreibungId} bk-grund-${k.kennung}`}
                      onClick={() => {
                        if (grund !== null) return;
                        if (!drin) beiHinzu(k);
                        else if (!einzige) beiEntfernen(k.kennung);
                      }}
                    >
                      <Sinnbild gewicht="light" name={kennzahlzeichen(k)} />
                      <span className="baustein__text">
                        {k.name}
                        {/* Auftrag CRM-65: Der Name bleibt der Name des Knopfs; die Zeile liest der Bildschirmleser als Beschreibung. */}
                        <small className="baustein__beschreibung" id={beschreibungId} aria-hidden="true">
                          {k.beschreibung}
                        </small>
                        {grund !== null && (
                          <small className="baustein__grund" id={`bk-grund-${k.kennung}`}>
                            {grund}
                          </small>
                        )}
                      </span>
                    </button>
                  );
                })}
            </div>
          ))}
          {gefunden.length === 0 && <p className="hilfe">Keine Kennzahl passt zur Suche.</p>}
        </div>
        <div className="knopfreihe knopfreihe--abschluss">
          <button type="button" className="knopf knopf--haupt" onClick={beiSchliessen}>
            Fertig
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Pop-up „Gruppierung wählen" bzw. „Aufteilung wählen": eine Wahl schließt
 * es. Aufgeteilt wird nur nach Merkmalen — die Zeiten stehen auf der Achse
 * (Auftrag CRM-46). Unpassendes steht ausgegraut mit Grund da (auswahl.ts).
 */
function Gruppenwahl({
  art,
  katalog,
  beschreibung,
  gewaehlt,
  beiWahl,
  beiSchliessen,
}: {
  readonly art: 'gruppierung' | 'aufteilung';
  readonly katalog: Kennzahlenkatalog;
  readonly beschreibung: Berichtsbeschreibung;
  readonly gewaehlt: readonly Katalogkennzahl[];
  readonly beiWahl: (g: Gruppierungskennung) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [suche, setzeSuche] = useState('');
  const gruppierung = art === 'gruppierung';
  const jetzt = gruppierung ? beschreibung.gruppierung : beschreibung.aufteilung;
  const gruppen = katalog.gruppierungen.filter((g) => (gruppierung || !g.zeitlich) && gruppierungTrifft(g, suche));
  const kopf = gruppierung ? 'Gruppierung' : 'Aufteilung';
  return (
    <Dialog
      titel={`${kopf} wählen`}
      unterzeile={gruppierung ? undefined : 'Je Wert eine Reihe im Bild.'}
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      <div className="bausteinwahl">
        <Bausteinsuche wert={suche} text={`${kopf} suchen`} beiAenderung={setzeSuche} />
        <div className="bausteinwahl__liste" role="group" aria-label={gruppierung ? 'Gruppierungen' : 'Merkmale zum Aufteilen'}>
          {gruppen.map((g) => {
            const grund = gruppierung ? gruppierungsgrund(g, gewaehlt) : aufteilungsgrund(g, gewaehlt, beschreibung);
            const grundId = `bk-${art}-grund-${g.kennung}`;
            const beschreibungId = `bk-${art}-beschreibung-${g.kennung}`;
            return (
              <button
                key={g.kennung}
                type="button"
                className="baustein"
                aria-pressed={g.kennung === jetzt}
                aria-disabled={grund !== null}
                aria-describedby={grund === null ? beschreibungId : `${beschreibungId} ${grundId}`}
                onClick={() => {
                  if (grund === null) beiWahl(g.kennung);
                }}
              >
                <Sinnbild gewicht="light" name={g.zeitlich ? 'calendar-blank' : 'stack'} />
                <span className="baustein__text">
                  {g.name}
                  <small className="baustein__beschreibung" id={beschreibungId} aria-hidden="true">
                    {g.beschreibung}
                  </small>
                  {grund !== null && (
                    <small className="baustein__grund" id={grundId}>
                      {grund}
                    </small>
                  )}
                </span>
              </button>
            );
          })}
          {gruppen.length === 0 && (
            <p className="hilfe">{gruppierung ? 'Keine Gruppierung passt zur Suche.' : 'Kein Merkmal passt zur Suche.'}</p>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/* ── Mitte: das Ergebnis ─────────────────────────────────────────────────── */

function Ergebnisansicht({
  ergebnis,
  anzeige,
  darstellung,
  beiDarstellung,
  beiKlassen,
  beiZelle,
}: {
  readonly ergebnis: Abfrageergebnis;
  /** Auftrag CRM-47: was der Bericht zeigt. */
  readonly anzeige: Berichtsanzeige;
  readonly darstellung: Darstellung;
  readonly beiDarstellung: (d: Darstellung) => void;
  /** AUS-36: Klassengrenzen und Form des Histogramms. */
  readonly beiKlassen: (t: Pick<Berichtsbeschreibung, 'klassengrenzen' | 'histogrammForm'>) => void;
  /** AUS-07: die Belege einer Zelle zeigen. */
  readonly beiZelle: (zelle: Zelle) => void;
}): JSX.Element {
  const erste = ergebnis.kennzahlen[0];
  /* Auftrag CRM-42: die Achsen nach Einheit — links die der ersten Kennzahl, rechts eine andere. */
  const { seiten } = achsenDerAuswahl(ergebnis.kennzahlen);
  const links = ergebnis.kennzahlen.filter((_, i) => seiten[i] === 'links');
  const rechts = ergebnis.kennzahlen.filter((_, i) => seiten[i] === 'rechts');
  const gruppe = ergebnis.gruppierung === null ? '' : ` je ${ergebnis.gruppierung.name}`;
  const teil = ergebnis.aufteilung == null ? '' : `, aufgeteilt nach ${ergebnis.aufteilung.gruppierung.name}`;
  const name = `${aufzaehlung(ergebnis.kennzahlen.map((k) => k.name))}${gruppe}${teil}`;
  /* Auftrag CRM-46: gestapelt nur mit Aufteilung und nur, was sich addiert. */
  const stapelNicht = gestapeltUntauglich(ergebnis);
  /* Der Kreis nur, wo er Anteile zeigt; sonst Balken mit dem Grund daneben (29.09.2026). */
  const untauglich = kreisUntauglich(ergebnis);
  const zeigeKreis = darstellung === 'kreis' && untauglich === null;
  /* Auftrag #31: nach Wochentag gibt es keinen Verlauf — Balken statt Linie. */
  const linieNicht = linieUntauglich(ergebnis);
  const wirksam = wirksameDarstellung(darstellung, ergebnis);
  /* AUS-40: viele Gruppen oder lange Namen legen die Balken von selbst hin — wie im PDF. */
  const art = wirksam === 'balken' && liegendEmpfohlen(ergebnis) ? 'liegend' : wirksam;
  /* AUS-34 (ÄA-56, Auftrag CRM-92): die Kennzahloptionen im Bild — was die gezeigte Darstellung eindeutig zeichnen kann. */
  const optionen = bildoptionen(ergebnis, art === 'kreis' && !zeigeKreis ? 'balken' : art, optionenGezeigt(anzeige));
  const bild =
    art === 'tabelle' || zeigeKreis || istDiagrammart(art)
      ? undefined
      : reihenAus(ergebnis, art === 'kreis' ? 'balken' : art, {
          vergleiche: anzeige.vergleiche,
          ziele: anzeige.ziele,
          optionen,
        });
  /* AUS-34: Steht die laufende Summe auf der sonst leeren rechten Achse, traegt die deren Format und Titel. */
  const kumRechts = bild?.rechtsKumuliert ?? null;
  const achseRechts = rechts[0] ?? kumRechts;
  const titelRechts = rechts[0] !== undefined ? achsentitel(rechts) : kumRechts === null ? null : `${kumRechts.name} kumuliert (${einheitszeichen(kumRechts.einheit)})`;
  /* AUS-10 bleibt Pflicht: Ohne Legende steht ihr Eintrag zur Schraffur allein da. */
  // AUS-10 und AUS-34 (ÄA-56): Schraffur und Lagezeichen bleiben erklaert, auch ohne Legende der Reihen.
  const legende = bild === undefined ? [] : anzeige.legende ? bild.legende : bild.legende.filter((l) => l.muster !== 'balken' && l.muster !== 'linie');

  return (
    <>
      <div className="ergebniskopf">
        <div>
          {ergebnis.kennzahlen.map((k, i) => {
            const w = ergebnis.gesamt.werte[i];
            return (
              <p key={k.kennung} className="ergebniskopf__zahl">
                {k.name}: {wertText(w?.wert ?? null, k.einheit)}
                {minMaxAn(ergebnis, k) && (
                  <span className="ergebniskopf__vergleich">
                    {' '}
                    · min {wertText(w?.min ?? null, k.einheit)} · max {wertText(w?.max ?? null, k.einheit)}
                  </span>
                )}
                {/* AUS-31, AUS-32 (Auftrag CRM-88): Median und n des ganzen Zeitraums. */}
                {optionAn(ergebnis, k, 'median') && (
                  <span className="ergebniskopf__vergleich"> · Median {wertText(w?.median ?? null, k.einheit)}</span>
                )}
                {optionAn(ergebnis, k, 'n') && <span className="ergebniskopf__vergleich"> · n {w?.n ?? '—'}</span>}
                {ergebnis.vergleiche.map((v, j) => (
                  <span key={v.art + String(j)} className="ergebniskopf__vergleich">
                    {' '}
                    · {v.name} {wertText(w?.vergleiche[j]?.wert ?? null, k.einheit)} (
                    {veraenderungText(w?.vergleiche[j]?.veraenderung ?? null, k.aggregat === 'quote')})
                  </span>
                ))}
              </p>
            );
          })}
          <p className="ergebniskopf__zusatz">
            {tagText(ergebnis.zeitraum.von)} – {tagText(ergebnis.zeitraum.bis)}
            {ergebnis.unvollstaendig.laufenderZeitraum ? ' · läuft noch' : ' · abgeschlossen'}
            {ergebnis.vergleiche.map((v) => (
              <span key={v.art + v.von}>
                {' '}
                · {v.name}: {tagText(v.von)} – {tagText(v.bis)}
                {v.bisZumSelbenTag ? ' (bis zum selben Tag)' : ''}
              </span>
            ))}
          </p>
        </div>
        <Segmente
          leicht
          beschriftung="Darstellung"
          gewaehlt={darstellung}
          beiWahl={beiDarstellung}
          /*
            Auftrag #33: Linie und Kreis stehen immer da. Wo sie nicht taugen,
            sind sie gesperrt und sagen, warum und wann es sie gibt — wer nach
            Monat gruppiert, soll den Kreis finden und lesen, was ihm fehlt.
          */
          optionen={[
            { wert: 'balken', text: 'Balken', vorne: <Sinnbild gewicht="light" name="chart-bar" /> },
            {
              wert: 'gestapelt',
              text: 'Gestapelt',
              vorne: <Sinnbild gewicht="light" name="stack" />,
              ...(stapelNicht === null ? {} : { gesperrt: { grund: stapelNicht, wann: GESTAPELT_BEDINGUNG } }),
            },
            {
              wert: 'linie',
              text: 'Linie',
              vorne: <Sinnbild gewicht="light" name="chart-line-up" />,
              ...(linieNicht === null ? {} : { gesperrt: { grund: linieNicht, wann: LINIE_BEDINGUNG } }),
            },
            { wert: 'tabelle', text: 'Tabelle', vorne: <Sinnbild gewicht="light" name="table" /> },
            {
              wert: 'kreis',
              text: 'Kreis',
              vorne: <Sinnbild gewicht="light" name="chart-pie" />,
              ...(untauglich === null ? {} : { gesperrt: { grund: untauglich, wann: KREIS_BEDINGUNG } }),
            },
          ]}
        />
      </div>
      {/* AUS-41: die weiteren Darstellungen — jede steht da, gesperrt mit Grund, wo sie nicht passt. */}
      <div className="ergebniskopf__weitere">
        <Segmente
          leicht
          beschriftung="Weitere Darstellungen"
          gewaehlt={darstellung}
          beiWahl={beiDarstellung}
          optionen={DIAGRAMMARTEN.map((d) => {
            const grund = diagrammartUntauglich(d, ergebnis);
            return { wert: d, text: DARSTELLUNGSNAMEN[d], ...(grund === null ? {} : { gesperrt: { grund, wann: DIAGRAMMART_BEDINGUNG[d] } }) };
          })}
        />
      </div>
      {darstellung === 'histogramm' && <Klassenwahl ergebnis={ergebnis} beiKlassen={beiKlassen} />}

      {ergebnis.unvollstaendig.hinweise.length > 0 && (
        <ul className="ergebniskopf__hinweise">
          {ergebnis.unvollstaendig.hinweise.map((h) => (
            <li key={h} className="hinweiszeile">
              {h}
            </li>
          ))}
        </ul>
      )}

      {stapelNicht !== null && darstellung === 'gestapelt' && (
        <p className="hinweiszeile" role="status">
          Nicht gestapelt: {stapelNicht} Gezeigt werden Balken nebeneinander.
        </p>
      )}
      {linieNicht !== null && darstellung === 'linie' && (
        <p className="hinweiszeile" role="status">
          Keine Linie: {linieNicht} Gezeigt werden Balken.
        </p>
      )}
      {istDiagrammart(darstellung) && art !== darstellung && (
        <p className="hinweiszeile" role="status">
          {darstellungstext(ergebnis)}
        </p>
      )}
      {optionen.hinweis !== null && (
        <p className="hinweiszeile" role="status">
          {optionen.hinweis}
        </p>
      )}
      {istDiagrammart(art) && erste !== undefined && ergebnis.zeilen.length > 0 && (
        <Diagrammartbild art={art} ergebnis={ergebnis} format={achsenformat(erste.einheit)} optionen={optionen} />
      )}
      {darstellung === 'kreis' && untauglich !== null && (
        <p className="hinweiszeile" role="status">
          Kein Kreis: {untauglich} Gezeigt werden Balken.
        </p>
      )}
      {zeigeKreis && erste !== undefined && (
        <>
          <Kreisdiagramm
            stuecke={kreisAus(ergebnis)}
            beschriftung={`${erste.name}${gruppe}, Anteile — die Zahlen stehen in der Tabelle darunter`}
            ohneLegende={!anzeige.legende}
            anteileImBild={optionen.anteil}
          />
          {ergebnis.vergleiche.length > 0 && (
            <p className="feld__hilfe">Vergleiche nur in der Tabelle.</p>
          )}
        </>
      )}
      {bild !== undefined && erste !== undefined && ergebnis.zeilen.length > 0 && (
        <>
          {legende.length > 0 && <Legende eintraege={legende} />}
          <Diagramm
            kategorien={bild.kategorien}
            reihen={bild.reihen}
            gestapelt={bild.gestapelt}
            beschriftung={`${name}, ${String(ergebnis.zeilen.length)} Werte${bild.zeichen == null ? '' : `. ${bild.zeichen}`} — die Zahlen stehen in der Tabelle darunter`}
            achse={achsenformat(erste.einheit)}
            gitter={anzeige.gitter}
            mitWerten={anzeige.werte === true}
            {...(achseRechts == null ? {} : { achseRechts: achsenformat(achseRechts.einheit) })}
            {...(!anzeige.achsentitel
              ? {}
              : { achsentitel: titelRechts === null ? { links: achsentitel(links) } : { links: achsentitel(links), rechts: titelRechts } })}
          />
          {bild.nurTabelle !== null && <p className="feld__hilfe">{bild.nurTabelle}</p>}
        </>
      )}
      {ergebnis.zeilen.length === 0 && ergebnis.gruppierung !== null && (
        <p className="hilfe">Keine Daten im Zeitraum.</p>
      )}
      {/*
        NFA-14: Die Tabelle bleibt die zugaengliche Form derselben Zahlen — die
        Anzeige klappt sie hoechstens zu (Auftrag CRM-47). Zu ist sie eine
        Zeile, die sie mit Maus und Tastatur oeffnet.
      */}
      {anzeige.tabelle === 'offen' ? (
        <>
          <p className="feld__hilfe">Ein Klick auf eine Zahl zeigt ihre Belege.</p>
          <Ergebnistabelle ergebnis={ergebnis} beiZelle={(z) => beiZelle(z)} summen={anzeige.summen} ziele={anzeige.ziele} />
        </>
      ) : (
        <details className="ergebnisdetails">
          <summary className="ergebnisdetails__kopf">
            <Sinnbild gewicht="light" name="table" />
            Tabelle zeigen ({String(ergebnis.zeilen.length)} {ergebnis.zeilen.length === 1 ? 'Zeile' : 'Zeilen'})
          </summary>
          <p className="feld__hilfe">Ein Klick auf eine Zahl zeigt ihre Belege.</p>
          <Ergebnistabelle ergebnis={ergebnis} beiZelle={(z) => beiZelle(z)} summen={anzeige.summen} ziele={anzeige.ziele} />
        </details>
      )}
    </>
  );
}

/**
 * AUS-36: die Klassen des Histogramms — runde Grenzen aus Min und Max oder
 * eigene, und die Form bei mehreren Gruppen. Die Pruefung der Grenzen
 * (steigend, hoechstens zwoelf) macht der Kontrakt; hier steht ihr Grund.
 */
function Klassenwahl({
  ergebnis,
  beiKlassen,
}: {
  readonly ergebnis: Abfrageergebnis;
  readonly beiKlassen: (t: Pick<Berichtsbeschreibung, 'klassengrenzen' | 'histogrammForm'>) => void;
}): JSX.Element {
  const b = ergebnis.beschreibung;
  const [text, setzeText] = useState((b.klassengrenzen ?? []).join('; ').replace(/\./g, ','));
  const [fehler, setzeFehler] = useState<string | null>(null);
  const uebernehmen = (): void => {
    const grenzen = grenzenAusText(text);
    if (grenzen.length === 0) {
      setzeFehler(null);
      beiKlassen({ klassengrenzen: undefined, histogrammForm: b.histogrammForm });
      return;
    }
    const grund = grenzenGrund(grenzen);
    setzeFehler(grund);
    if (grund === null) beiKlassen({ klassengrenzen: [...grenzen], histogrammForm: b.histogrammForm });
  };
  return (
    <div className="klassenwahl">
      <label className="feld klassenwahl__grenzen">
        <span className="feld__beschriftung">Klassengrenzen</span>
        <input
          className="feld__eingabe"
          value={text}
          placeholder="leer: runde Grenzen aus Min und Max"
          aria-describedby="klassenwahl-hilfe"
          aria-invalid={fehler !== null}
          onChange={(e) => setzeText(e.target.value)}
          onBlur={uebernehmen}
          onKeyDown={(e) => {
            if (e.key === 'Enter') uebernehmen();
          }}
        />
        <span id="klassenwahl-hilfe" className="feld__hilfe">
          {fehler ?? 'Etwa „250; 500; 1000".'}
        </span>
      </label>
      {ergebnis.zeilen.length > 1 && (
        <Segmente
          leicht
          beschriftung="Form"
          gewaehlt={b.histogrammForm ?? 'vielfache'}
          beiWahl={(f) => beiKlassen({ klassengrenzen: b.klassengrenzen, histogrammForm: f })}
          optionen={[
            { wert: 'vielfache', text: 'Je Gruppe ein Bild' },
            { wert: 'gestapelt', text: 'Gestapelt' },
          ]}
        />
      )}
    </div>
  );
}

/* Auftrag CRM-47: die Schalter der Anzeige, in der Folge, in der man auf den Bericht schaut. */
const ANZEIGETEILE: readonly { readonly teil: Exclude<keyof Berichtsanzeige, 'tabelle' | 'spanne'>; readonly text: string }[] = [
  { teil: 'legende', text: 'Legende' },
  { teil: 'werte', text: 'Werte an Balken und Punkten' },
  { teil: 'gitter', text: 'Gitterlinien' },
  { teil: 'achsentitel', text: 'Achsentitel' },
  { teil: 'vergleiche', text: 'Vergleiche im Bild' },
  { teil: 'ziele', text: 'Zielwerte' },
  /* AUS-34 (ÄA-56): Min/Max, Median, Quartile, Anteil, kumuliert und n auch im Diagramm; fehlt = an. */
  { teil: 'optionen', text: 'Optionen im Diagramm zeigen' },
  { teil: 'summen', text: 'Summen der Tabelle' },
];

/**
 * WAS DER BERICHT ZEIGT (Auftrag CRM-47). Jeder Haken aendert die
 * Beschreibung und gilt auch im PDF und im Widget. Pflichtteile stehen nicht
 * zur Wahl: Die Tabelle laesst sich nur zuklappen (NFA-14), der laufende
 * Zeitraum bleibt gekennzeichnet (AUS-10).
 */
function Anzeigewahl({
  anzeige,
  beiAnzeige,
}: {
  readonly anzeige: Berichtsanzeige;
  readonly beiAnzeige: (a: Berichtsanzeige) => void;
}): JSX.Element {
  return (
    <fieldset className="anzeigewahl">
      <legend className="feld__beschriftung">Anzeige</legend>
      {ANZEIGETEILE.map(({ teil, text }) => (
        <label key={teil} className="anzeigewahl__eintrag">
          <input
            type="checkbox"
            checked={teil === 'optionen' ? optionenGezeigt(anzeige) : anzeige[teil] === true}
            onChange={(e) => beiAnzeige({ ...anzeige, [teil]: e.target.checked })}
          />
          {text}
        </label>
      ))}
      <label className="anzeigewahl__eintrag">
        <input
          type="checkbox"
          checked={anzeige.tabelle === 'offen'}
          onChange={(e) => beiAnzeige({ ...anzeige, tabelle: e.target.checked ? 'offen' : 'zu' })}
        />
        Tabelle aufgeklappt
      </label>
      <p className="feld__hilfe">Gilt auch für PDF und Dashboard.</p>
    </fieldset>
  );
}

/* ── Links unter den Bausteinen: Zeitraum, Filter, Definition (Auftrag CRM-101) ── */

function Einstellungen({
  katalog,
  gewaehlt,
  beschreibung,
  ergebnis,
  beiZeitraum,
  beiFilter,
  beiVergleichen,
  beiAnzeige,
}: {
  readonly katalog: Kennzahlenkatalog;
  readonly gewaehlt: readonly Katalogkennzahl[];
  readonly beschreibung: Berichtsbeschreibung;
  readonly ergebnis: Abfrageergebnis | undefined;
  readonly beiZeitraum: (z: Berichtszeitraum) => void;
  readonly beiFilter: (f: Berichtsbeschreibung['filter']) => void;
  readonly beiVergleichen: (v: Berichtsbeschreibung['vergleiche']) => void;
  readonly beiAnzeige: (a: Berichtsanzeige) => void;
}): JSX.Element {
  /* Filtern laesst sich nur nach Merkmalen, die ALLE gewaehlten Kennzahlen kennen (Auftrag #26, CRM-42). */
  const filterbar = gemeinsameFilter(gewaehlt);
  const z = beschreibung.zeitraum;
  const [nach, setzeNach] = useState<Gruppierungskennung | ''>('');
  const [werte, setzeWerte] = useState<readonly { schluessel: string; bezeichnung: string }[] | undefined>(undefined);
  const [wert, setzeWert] = useState('');
  /** Die Namen der gefilterten Werte — für die Marken (Produkt steht als Kennung im Filter). */
  const [namen, setzeNamen] = useState<ReadonlyMap<string, string>>(new Map());

  const gruppenname = useCallback(
    (g: string) => katalog.gruppierungen.find((x) => x.kennung === g)?.name ?? g,
    [katalog],
  );

  /*
   * DIE WERTE EINES FILTERS KOMMEN AUS DERSELBEN ABFRAGE, gruppiert nach dem
   * Merkmal — also genau die, die es im Zeitraum und im Sichtbarkeitsbereich
   * gibt. Eine zweite Schnittstelle für „mögliche Werte" wäre eine zweite
   * Stelle, die Rechte kennen müsste.
   */
  const werteLaden = useCallback(
    async (g: Gruppierungskennung) => {
      const e = await abfrage({
        ...beschreibung,
        gruppierung: g,
        // Auftrag CRM-46: Die Werte eines Merkmals kommen ohne Aufteilung — sie koennte das Merkmal selbst sein.
        aufteilung: null,
        filter: beschreibung.filter.filter((f) => f.nach !== g),
      });
      const liste = e.zeilen.map((zeile) => ({
        schluessel: zeile.schluessel ?? '',
        bezeichnung: zeile.bezeichnung,
      }));
      setzeNamen((m) => new Map([...m, ...liste.map((l) => [`${g}:${l.schluessel}`, l.bezeichnung] as const)]));
      return liste;
    },
    [beschreibung],
  );

  /* Die Namen der schon gesetzten Filter einmal nachladen (ein geöffneter Bericht). */
  const filterKey = beschreibung.filter.map((f) => f.nach).join(',');
  const geladen = useRef(new Set<string>());
  useEffect(() => {
    for (const f of beschreibung.filter) {
      if (geladen.current.has(f.nach)) continue;
      geladen.current.add(f.nach);
      void werteLaden(f.nach as Gruppierungskennung).catch(() => undefined);
    }
    // Nur bei neuen Merkmalen — werteLaden aendert sich mit jeder Beschreibung.
  }, [filterKey]);

  const marken = useMemo(
    () =>
      beschreibung.filter.flatMap((f) =>
        f.ist.map((w) => ({
          text: `${gruppenname(f.nach)}: ${w === '' ? 'ohne Angabe' : (namen.get(`${f.nach}:${w}`) ?? w)}`,
          entfernen: () =>
            beiFilter(
              beschreibung.filter
                .map((x) => (x.nach === f.nach ? { ...x, ist: x.ist.filter((y) => y !== w) } : x))
                .filter((x) => x.ist.length > 0),
            ),
        })),
      ),
    [beschreibung.filter, namen, gruppenname, beiFilter],
  );

  function hinzufuegen(e: FormEvent): void {
    e.preventDefault();
    if (nach === '' || werte === undefined) return;
    const bisher = beschreibung.filter.find((f) => f.nach === nach);
    const neu = bisher === undefined
      ? [...beschreibung.filter, { nach, ist: [wert] }]
      : beschreibung.filter.map((f) => (f.nach === nach && !f.ist.includes(wert) ? { ...f, ist: [...f.ist, wert] } : f));
    beiFilter(neu);
    setzeNach('');
    setzeWerte(undefined);
    setzeWert('');
  }

  return (
    <section className="karte baukasten__einstellungen" aria-labelledby="bk-einstellungen">
      <div className="karte__titel">
        <h2 id="bk-einstellungen">Einstellungen</h2>
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="bk-zeitraum">
          Zeitraum
        </label>
        <select
          id="bk-zeitraum"
          className="feld__eingabe"
          value={z.art === 'relativ' ? z.wert : 'frei'}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'frei') {
              const r = ergebnis?.zeitraum ?? { von: `${new Date().getFullYear()}-01-01`, bis: `${new Date().getFullYear()}-12-31` };
              beiZeitraum({ art: 'frei', von: r.von, bis: r.bis });
            } else {
              beiZeitraum({ art: 'relativ', wert: v as RelativerZeitraum });
            }
          }}
        >
          {RELATIV.map((r) => (
            <option key={r} value={r}>
              {ZEITRAUMNAMEN[r]}
            </option>
          ))}
          <option value="frei">Frei wählen</option>
        </select>
      </div>
      {z.art === 'frei' && (
        <div className="zeitraumfelder">
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bk-von">
              Von
            </label>
            <input
              id="bk-von"
              className="feld__eingabe"
              type="date"
              value={z.von}
              onChange={(e) => e.target.value !== '' && beiZeitraum({ ...z, von: e.target.value })}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bk-bis">
              Bis
            </label>
            <input
              id="bk-bis"
              className="feld__eingabe"
              type="date"
              value={z.bis}
              onChange={(e) => e.target.value !== '' && beiZeitraum({ ...z, bis: e.target.value })}
            />
          </div>
        </div>
      )}

      <div className="feld">
        <span className="feld__beschriftung">Filter</span>
        {marken.length === 0 ? <p className="feld__hilfe">Kein Filter gesetzt.</p> : <Filtermarken marken={marken} />}
      </div>
      {filterbar.length > 0 && (
        <form className="filterwahl" onSubmit={hinzufuegen}>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bk-filter-nach">
              Filter hinzufügen
            </label>
            <select
              id="bk-filter-nach"
              className="feld__eingabe"
              value={nach}
              onChange={(e) => {
                const g = e.target.value as Gruppierungskennung | '';
                setzeNach(g);
                setzeWerte(undefined);
                setzeWert('');
                if (g !== '') {
                  void werteLaden(g)
                    .then((l) => {
                      setzeWerte(l);
                      setzeWert(l[0]?.schluessel ?? '');
                    })
                    .catch(() => setzeWerte([]));
                }
              }}
            >
              <option value="">Merkmal wählen …</option>
              {filterbar.map((g) => (
                <option key={g} value={g}>
                  {gruppenname(g)}
                </option>
              ))}
            </select>
          </div>
          {nach !== '' && (
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="bk-filter-wert">
                {gruppenname(nach)}
              </label>
              {werte === undefined ? (
                <p className="feld__hilfe" role="status">
                  Werte werden geladen …
                </p>
              ) : werte.length === 0 ? (
                <p className="feld__hilfe">Keine Werte im Zeitraum.</p>
              ) : (
                <select id="bk-filter-wert" className="feld__eingabe" value={wert} onChange={(e) => setzeWert(e.target.value)}>
                  {werte.map((w) => (
                    <option key={w.schluessel} value={w.schluessel}>
                      {w.bezeichnung}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {nach !== '' && werte !== undefined && werte.length > 0 && (
            <button type="submit" className="knopf knopf--klein">
              Filter setzen
            </button>
          )}
        </form>
      )}

      <Vergleichswahl vergleiche={beschreibung.vergleiche} beiVergleichen={beiVergleichen} />

      <Anzeigewahl anzeige={anzeigeVon(beschreibung)} beiAnzeige={beiAnzeige} />

      {/* AUS-01: Die Definition jeder Kennzahl steht seit Auftrag CRM-107 hinter ihrem i-Knopf in den Bausteinen. */}
      <p className="hinweiszeile">
        <Sinnbild gewicht="light" name="shield-check" />
        Nur Zahlen, die Sie auch einzeln sehen dürfen.
      </p>
    </section>
  );
}

/* ── Der Dialog für Titel beim Speichern und Umbenennen ─────────────────── */

/**
 * Exportieren (AUS-08, Auftrag #28): CSV, XLSX oder PDF, jedes mit dem Kopf
 * aus Bericht, Definitionen, Filtern, Zeitraum, Zeitpunkt, Benutzer und Stand.
 * Das XLSX traegt auf Wunsch ein zweites Blatt mit den Belegen (AUS-07).
 */
function Exportdialog({
  beiSchliessen,
  beiExport,
}: {
  readonly beiSchliessen: () => void;
  readonly beiExport: (format: Exportformat, mitBelegen: boolean) => Promise<void>;
}): JSX.Element {
  const [format, setzeFormat] = useState<Exportformat>('xlsx');
  const [mitBelegen, setzeMitBelegen] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  const formate: readonly { wert: Exportformat; text: string; hilfe: string }[] = [
    { wert: 'xlsx', text: 'XLSX', hilfe: 'Tabelle mit Zahlenformaten' },
    { wert: 'pdf', text: 'PDF', hilfe: 'Diagramm und Tabelle' },
    { wert: 'csv', text: 'CSV', hilfe: 'Text mit Semikolon' },
  ];

  async function absenden(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (laeuft) return;
    setzeLaeuft(true);
    try {
      await beiExport(format, format === 'xlsx' && mitBelegen);
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <Dialog
      titel="Bericht exportieren"
      unterzeile="Mit Definition, Filtern, Zeitraum und Stand im Kopf."
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      <form onSubmit={(e) => void absenden(e)}>
        <fieldset className="feld">
          <legend className="feld__beschriftung">Format</legend>
          {formate.map((f) => (
            <label key={f.wert} className="liste__schalter">
              <input
                type="radio"
                name="exportformat"
                value={f.wert}
                checked={format === f.wert}
                onChange={() => setzeFormat(f.wert)}
              />{' '}
              {f.text} <small className="feld__hilfe">— {f.hilfe}</small>
            </label>
          ))}
        </fieldset>
        {format === 'xlsx' && (
          <label className="liste__schalter">
            <input type="checkbox" checked={mitBelegen} onChange={(e) => setzeMitBelegen(e.target.checked)} /> Zweites
            Blatt mit den Belegen
          </label>
        )}
        <div className="knopfreihe knopfreihe--abschluss">
          <button type="button" className="knopf" onClick={beiSchliessen}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt" disabled={laeuft}>
            Herunterladen
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * Teilen (AUS-16, Auftrag #27): privat, mit dem eigenen Team oder mit allen,
 * die Berichte sehen. „Mit allen" nur mit auswertung:teilen (AeA-42) — ohne
 * das Recht steht die Wahl da, ist aber gesperrt und sagt warum.
 */
function Teilendialog({
  freigabe,
  darfAlle,
  beiSchliessen,
  beiFreigabe,
}: {
  readonly freigabe: Berichtsfreigabe;
  readonly darfAlle: boolean;
  readonly beiSchliessen: () => void;
  readonly beiFreigabe: (f: Berichtsfreigabe) => Promise<void>;
}): JSX.Element {
  const [wahl, setzeWahl] = useState<Berichtsfreigabe>(freigabe);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const stufen: readonly { wert: Berichtsfreigabe; text: string; hilfe: string; gesperrt: boolean }[] = [
    { wert: 'privat', text: 'Privat', hilfe: 'Nur Sie sehen den Bericht.', gesperrt: false },
    { wert: 'team', text: 'Mein Team', hilfe: 'Ihr Team liest mit.', gesperrt: false },
    {
      wert: 'alle',
      text: 'Alle',
      hilfe: darfAlle ? 'Alle mit Recht auf Berichte lesen mit.' : 'Dafür fehlt Ihnen ein eigenes Recht.',
      gesperrt: !darfAlle,
    },
  ];

  async function absenden(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (laeuft) return;
    setzeLaeuft(true);
    try {
      await beiFreigabe(wahl);
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <Dialog
      titel="Bericht teilen"
      unterzeile="Jeder sieht darin nur, was er sehen darf."
      breite="schmal"
      beiSchliessen={beiSchliessen}
    >
      <form onSubmit={(e) => void absenden(e)}>
        {fehler !== undefined && <Meldung fehler={fehler} />}
        <fieldset className="feld">
          <legend className="feld__beschriftung">Freigabe</legend>
          {stufen.map((s) => (
            <label key={s.wert} className="liste__schalter">
              <input
                type="radio"
                name="freigabe"
                value={s.wert}
                checked={wahl === s.wert}
                disabled={s.gesperrt}
                onChange={() => setzeWahl(s.wert)}
              />{' '}
              {s.text} <small className="feld__hilfe">— {s.hilfe}</small>
            </label>
          ))}
        </fieldset>
        <div className="knopfreihe knopfreihe--abschluss">
          <button type="button" className="knopf" onClick={beiSchliessen}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt" disabled={laeuft || wahl === freigabe}>
            Übernehmen
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function Titeldialog({
  titel,
  unterzeile,
  vorgabe: anfang,
  knopf,
  beiSchliessen,
  beiTitel,
}: {
  readonly titel: string;
  readonly unterzeile?: string;
  readonly vorgabe: string;
  readonly knopf: string;
  readonly beiSchliessen: () => void;
  readonly beiTitel: (titel: string) => Promise<void>;
}): JSX.Element {
  const [wert, setzeWert] = useState(anfang);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const leer = wert.trim() === '';

  async function absenden(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (leer || laeuft) return;
    setzeLaeuft(true);
    try {
      await beiTitel(wert.trim());
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <Dialog titel={titel} unterzeile={unterzeile} breite="schmal" beiSchliessen={beiSchliessen}>
      <form noValidate onSubmit={(e) => void absenden(e)}>
        {fehler !== undefined && <Meldung fehler={fehler} gezeigteFelder={['titel']} />}
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="bk-titel">
            Titel
          </label>
          <input
            id="bk-titel"
            className="feld__eingabe"
            value={wert}
            maxLength={120}
            aria-invalid={leer}
            aria-describedby="bk-titel-hilfe"
            onChange={(e) => setzeWert(e.target.value)}
          />
          <p className="feld__hilfe" id="bk-titel-hilfe">
            Höchstens 120 Zeichen.
          </p>
        </div>
        <div className="knopfreihe knopfreihe--abschluss">
          <button type="button" className="knopf" onClick={beiSchliessen}>
            Abbrechen
          </button>
          <button type="submit" className="knopf knopf--haupt" disabled={leer || laeuft}>
            {knopf}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ── Die Vergleichszeitraeume (Auftrag #26, AUS-02) ──────────────────────── */

const VERGLEICHSWAHL: readonly Vergleichsart[] = ['vorjahr', 'vor_zwei_jahren', 'vorperiode', 'frei'];

function Vergleichswahl({
  vergleiche,
  beiVergleichen,
}: {
  readonly vergleiche: Berichtsbeschreibung['vergleiche'];
  readonly beiVergleichen: (v: Berichtsbeschreibung['vergleiche']) => void;
}): JSX.Element {
  const setze = (i: number, v: Berichtsvergleich): void =>
    beiVergleichen(vergleiche.map((x, j) => (j === i ? v : x)));
  const neu = (art: Vergleichsart): Berichtsvergleich => {
    if (art !== 'frei') return { art };
    const jahr = new Date().getFullYear() - 1;
    return { art, von: `${String(jahr)}-01-01`, bis: `${String(jahr)}-12-31` };
  };

  return (
    <fieldset className="vergleichswahl">
      <legend className="feld__beschriftung">Vergleich</legend>
      {vergleiche.map((v, i) => (
        <div key={String(i)} className="vergleichswahl__eintrag">
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={`bk-vergleich-${String(i)}`}>
              Vergleich {i + 1}
            </label>
            <select
              id={`bk-vergleich-${String(i)}`}
              className="feld__eingabe"
              value={v.art}
              onChange={(e) => setze(i, neu(e.target.value as Vergleichsart))}
            >
              {VERGLEICHSWAHL.map((a) => (
                <option key={a} value={a}>
                  {VERGLEICHSNAMEN[a]}
                </option>
              ))}
            </select>
          </div>
          {v.art === 'frei' && (
            <div className="zeitraumfelder">
              <div className="feld">
                <label className="feld__beschriftung" htmlFor={`bk-vergleich-${String(i)}-von`}>
                  Von
                </label>
                <input
                  id={`bk-vergleich-${String(i)}-von`}
                  className="feld__eingabe"
                  type="date"
                  value={v.von}
                  onChange={(e) => e.target.value !== '' && setze(i, { ...v, von: e.target.value })}
                />
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor={`bk-vergleich-${String(i)}-bis`}>
                  Bis
                </label>
                <input
                  id={`bk-vergleich-${String(i)}-bis`}
                  className="feld__eingabe"
                  type="date"
                  value={v.bis}
                  onChange={(e) => e.target.value !== '' && setze(i, { ...v, bis: e.target.value })}
                />
              </div>
            </div>
          )}
          <button
            type="button"
            className="knopf knopf--eingriff knopf--klein"
            onClick={() => beiVergleichen(vergleiche.filter((_, j) => j !== i))}
          >
            Vergleich {i + 1} entfernen
          </button>
        </div>
      ))}
      {vergleiche.length < 2 && (
        <button
          type="button"
          className="knopf knopf--klein"
          onClick={() => beiVergleichen([...vergleiche, neu(vergleiche.length === 0 ? 'vorjahr' : 'vorperiode')])}
        >
          Vergleich hinzufügen
        </button>
      )}
      <p className="feld__hilfe">Läuft der Zeitraum noch, endet der Vergleich am selben Tag.</p>
    </fieldset>
  );
}
